import { env } from '@/config/env'
import { notifyUnauthorized } from './authUnauthorized'

export type ApiError = Error & {
  status?: number
  details?: unknown
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

const API_BASE_URL = env.apiBaseUrl || 'http://localhost:5000/api'

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

async function parseError(response: Response): Promise<ApiError> {
  const err: ApiError = new Error(`Request failed: ${response.status} ${response.statusText}`)
  err.status = response.status

  try {
    const text = await response.text()
    if (!text) return err

    try {
      const json = JSON.parse(text)
      err.details = json
      const msg =
        json?.message ??
        json?.errorMessage ??
        json?.title ??
        (typeof json === 'string' ? json : null)
      if (msg) err.message = msg
    } catch {
      // Non-JSON error body
      err.details = text
      err.message = text
    }
  } catch {
    // ignore
  }

  return err
}

export async function httpRequest<TResponse>(
  path: string,
  options: {
    method?: HttpMethod
    query?: Record<string, string | number | boolean | undefined | null>
    body?: unknown
    signal?: AbortSignal
  } = {},
): Promise<TResponse> {
  const method = options.method ?? 'GET'

  const url = new URL(`${API_BASE_URL}${path}`)
  if (options.query) {
    Object.entries(options.query).forEach(([k, v]) => {
      if (v === undefined || v === null) return
      url.searchParams.set(k, String(v))
    })
  }

  const response = await fetch(url.toString(), {
    method,
    headers: getAuthHeaders(),
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (!response.ok) {
    if (response.status === 401) {
      const isMenuOrTenant = path.startsWith('/v1/menus') || path.startsWith('/v1/tenants')
      if (!isMenuOrTenant) notifyUnauthorized()
    }
    throw await parseError(response)
  }

  // DELETE often has empty body
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    return (undefined as unknown) as TResponse
  }

  return (await response.json()) as TResponse
}

